import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../user/permissions';
import { mappingColorStatus } from '../../widgets/eventListModal';
import { requiredField, useForm } from '../../../utils/validators';
import { Event, ModerationFlowsModel, Rate } from '../../../models';
import { attendeeStatusFilterOptions } from '../../../constants/moderation';

import {
    Accordion,
    Button,
    Form,
    FrameCard,
    Dropdown,
    Status,
    Section,
    SelectHierarchyLabel,
    SelectLabel,
    ToastContext,
    TooltipHelp,
    WeezHeader,
    ToggleContainer
} from '@weezevent/nacre';

import LoadingCard from '../../../components/loadingCard';

import css from './flows.module.css';

export default function FlowDetails({ organizationId, flowId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const permissions = usePermissions();
    const toast = React.useContext(ToastContext);
    const isAdmin = permissions.isModerationAdmin(organizationId);
    const [eventsSelected, setEventsSelected] = React.useState([]);
    const [eventSearch, setEventSearch] = React.useState('');

    const [form, setForm, setFormObject, formErrors] = useForm(
        {
            name: '',
            rates: [],
            allow_removed_attendees: false,
            status: {
                name: '',
                stage: ''
            }
        },
        { name: requiredField }
    );

    const [events, loadingEvents] = Event.useApiModel(
        {
            organizationId: organizationId,
            cache: false,
            allow_cache: false,
            query: {
                publication_status__in: ['draft', 'published'],
                search: eventSearch
            }
        },
        [organizationId, eventSearch]
    );

    const [, loadingFlow] = ModerationFlowsModel.useApiModel(
        {
            organizationId,
            id: flowId,
            cache: false,
            allow_cache: false,
            launch: Boolean(flowId),
            query: {
                expand: 'rates'
            },
            onSuccess: response => {
                setFormObject({ ...response.object });
                setEventsSelected(Array.from(new Set(response.rates.map(r => r.event))));
            }
        },
        [organizationId, flowId]
    );

    const backLink = React.useMemo(() => {
        if (!flowId) {
            return `/ticket/O${organizationId}/moderation`;
        }
        return `/ticket/O${organizationId}/moderation/moderation_flows/${flowId}/moderation_attendees`;
    }, [organizationId, flowId]);

    const handleSubmit = React.useCallback(() => {
        const ratesIds = Array.from(new Set(form?.rates.map(r => r.id)));
        const flow = new ModerationFlowsModel({ ...form, rates: ratesIds });

        flow.save({ organizationId })
            .then(() => {
                toast.success(t(`ticket.moderation.flows.${flow.id ? 'edit' : 'create'}.toastSuccess`));
                history.push(backLink);
            })
            .catch(() => {
                toast.error(t(`ticket.moderation.flows.${flow.id ? 'edit' : 'create'}.toastError`));
            });
    }, [form, organizationId, backLink]);

    const eventsOptions = React.useMemo(() => {
        if (!events || loadingEvents) {
            return [];
        }

        return events
            ?.filter(e => e.time_status !== 'over' && e.publication_status !== 'closed')
            .map(event => {
                return { ...event.toOptions(), text: <EventItem event={event} /> };
            });
    }, [events, loadingEvents]);

    const selectedEventsLabel = React.useMemo(() => {
        if (!eventsSelected) {
            return [];
        }
        const events = eventsOptions?.filter(e => eventsSelected.includes(e.value));

        if (!events?.length) {
            return null;
        }
        if (events?.length !== eventsOptions?.length) {
            return t('ticket.moderation.flows.create.eventsSelectedCount', { count: events.length });
        }
        return t('ticket.moderation.flows.create.eventsAllSelected');
    }, [eventsSelected, eventsOptions]);

    const disabled = React.useMemo(() => {
        if (!flowId) {
            return !form.name || !form.status.name || !form.status.stage;
        }
        return !form.name;
    }, [form, flowId]);

    return (
        <>
            <WeezHeader
                title={t(`ticket.moderation.flows.${flowId ? 'edit' : 'create'}.title`)}
                rightComponent={[<Button key={0} primary label={t('ticket.common.action.save')} disabled={disabled} onClick={handleSubmit} />]}
                backLink={{
                    onClick: () => {
                        history.push(backLink);
                    }
                }}
            >
                {flowId && t('ticket.moderation.flows.edit.subtitle')}
            </WeezHeader>
            <FrameCard title={t('ticket.moderation.flows.create.mainInfosTitle')} subtitle={t('ticket.moderation.flows.create.mainInfosSubtitle')} separator>
                <Form.Field>
                    <Form.Label label={t('ticket.moderation.flows.create.nameLabel')} />
                    <Form.Input
                        name="name"
                        placeholder={t('ticket.moderation.flows.create.nameLabel')}
                        value={form.name}
                        onChange={({ target: { value } }) => {
                            setForm('name', value);
                        }}
                        errors={Boolean(formErrors['name'])}
                    />
                </Form.Field>
                <Form.FieldErrors errors={[formErrors['name'] && t(formErrors['name'])]} />
            </FrameCard>
            {!flowId && (
                <FrameCard
                    title={t('ticket.moderation.statusesTransitions.statuses.detail.defaultStatusLabel')}
                    subtitle={t('ticket.moderation.statusesTransitions.statuses.detail.defaultStatusCardSubtitle')}
                    separator
                >
                    <Form.Field>
                        <Form.Label label={t('ticket.moderation.statusesTransitions.statuses.detail.nameLabel')} />
                        <Form.Input
                            name="name"
                            placeholder={t('ticket.moderation.statusesTransitions.statuses.detail.namePlaceholder')}
                            value={form.status.name}
                            onChange={({ target: { value } }) => setForm('status', { ...form.status, name: value })}
                        />
                    </Form.Field>
                    <Form.Field>
                        <SelectLabel
                            name="stage"
                            value={form.status.stage}
                            label={t('ticket.moderation.statusesTransitions.statuses.detail.typeLabel')}
                            onChange={value => setForm('status', { ...form.status, stage: value })}
                            options={attendeeStatusFilterOptions().map(option => ({ ...option, value: option.name }))}
                            placeholder={t('ticket.common.action.select')}
                        />
                    </Form.Field>
                </FrameCard>
            )}
            {isAdmin && (
                <FrameCard title={t('ticket.moderation.flows.create.settingsTitle')} subtitle={t('ticket.moderation.flows.create.settingsSubtitle')} separator>
                    <ToggleContainer
                        className={css['toggle-container']}
                        title={t('ticket.moderation.flows.create.attendees_toggle_label')}
                        onChange={({ target: { checked } }) => setForm('allow_removed_attendees', checked)}
                        checked={form.allow_removed_attendees}
                    />
                </FrameCard>
            )}
            <FrameCard title={t('ticket.moderation.flows.create.eventsTitle')} subtitle={t('ticket.moderation.flows.create.eventsSubtitle')} separator>
                <LoadingCard loading={flowId && loadingFlow}>
                    <div className={css['events-selector']}>
                        <SelectHierarchyLabel
                            label={
                                <div>
                                    {t('ticket.moderation.flows.create.eventLabel')}
                                    <TooltipHelp title={t('ticket.moderation.flows.create.eventTooltip')} />
                                </div>
                            }
                            multi
                            height="medium"
                            options={eventsOptions}
                            onExternalSearch={setEventSearch}
                            externalOptions={eventsOptions}
                            placeholder={t('ticket.common.action.select')}
                            placeholderSearch={t('ticket.common.action.search')}
                            onChange={value => setEventsSelected(value)}
                            selected={eventsSelected}
                            value={selectedEventsLabel}
                        />
                    </div>
                </LoadingCard>
                {events
                    ?.filter(e => eventsSelected.includes(e.id))
                    .map((event, index) => (
                        <EventRates key={index} event={event} organizationId={organizationId} setForm={setForm} form={form} flowId={flowId} />
                    ))}
            </FrameCard>
        </>
    );
}

const EventRates = React.memo(({ event, organizationId, setForm, form, flowId }) => {
    const { t } = useTranslation();
    const [ratesOptions, setRatesOptions] = React.useState([]);

    const [rates] = Rate.useApiModel(
        {
            organizationId: organizationId,
            eventId: event.id,
            onSuccess: response => {
                setRatesOptions(
                    response.map(r => ({
                        text: r.rate_template?.name ?? r.name,
                        value: r.id,
                        disabled: r.moderation_flow && r.moderation_flow !== parseInt(flowId)
                    }))
                );
            }
        },
        [organizationId, event]
    );

    const ratesSelectedValue = React.useMemo(() => Array.from(new Set(form.rates.map(r => r.id))), [form]);

    const selectedRatesLabel = React.useMemo(() => {
        if (!ratesSelectedValue) {
            return [];
        }
        const rates = ratesOptions?.filter(r => ratesSelectedValue.includes(r.value));

        if (!rates.length) {
            return null;
        }
        if (rates.length !== ratesOptions?.length) {
            return t('ticket.moderation.flows.create.ratesSelectedCount', { count: rates.length });
        }
        return t('ticket.moderation.flows.create.eventsAllSelected');
    }, [ratesOptions, form.rates]);

    const handleChange = React.useCallback(
        value => {
            const eventRates = rates.filter(r => value.includes(r.id));
            const formRates = form.rates.filter(r => r.event !== event.id);
            const _rates = eventRates.concat(formRates);
            const ids = Array.from(new Set(_rates.map(r => r.id)));

            const newRates = ids.map(i => {
                const rate = _rates.find(r => r.id === i);
                return rate;
            });

            setForm('rates', newRates);
        },
        [rates, form]
    );

    return (
        <Accordion openByDefault title={event.name}>
            <Section>
                <SelectHierarchyLabel
                    multi
                    label={
                        <div>
                            {t('ticket.moderation.flows.create.rateLabel')}
                            <TooltipHelp title={t('ticket.moderation.flows.create.rateTooltip')} />
                        </div>
                    }
                    height="mini"
                    options={ratesOptions}
                    placeholder={t('ticket.common.action.select')}
                    onChange={value => handleChange(value)}
                    selected={ratesSelectedValue}
                    value={selectedRatesLabel}
                    disabled={!ratesOptions?.length || ratesOptions.every(it => it.disabled)}
                />
            </Section>
        </Accordion>
    );
});

function EventItem({ event }) {
    const { i18n, t } = useTranslation();
    const start_date = event.start_date ? new Intl.DateTimeFormat(i18n.language).format(new Date(event.start_date)) : null;
    const subtitle = `${start_date} - ${event.id}`;
    const { publication_status } = event;

    return (
        <div className={css['event-item']}>
            <Dropdown.Item
                key={event.id}
                subtitle={subtitle}
                status={<Status responsive color={mappingColorStatus(publication_status)} text={t(`eventStatus.${publication_status}`)} />}
            >
                {event.name}
            </Dropdown.Item>
        </div>
    );
}
